import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Shield, Award, Clock, MapPin, Users, Zap } from "lucide-react";
import PageHero from "../components/PageHero";
import EquipmentFleet from "../components/EquipmentFleet";
import CrewSafetyFirst from "../components/CrewSafetyFirst";
import { useMedia } from "../hooks/useMedia";

const TIMELINE = [
  { year: "2013", title: "Founded", body: "Founded in Brooklyn by a US Army veteran on a single water-fed pole and a service philosophy: military-precision execution, always documented." },
  { year: "2015", title: "First Portfolio Client", body: "Signed our first multi-property retainer with a Manhattan restaurant group. The retainer is still active — same crew lead, same clipboard, ten years on." },
  { year: "2017", title: "DI Water Rig Fleet", body: "Custom-built a DI water rig fleet to service Class-A office curtain walls from the sidewalk. Ended the era of ladders on our sites." },
  { year: "2019", title: "North Carolina Expansion", body: "Opened a second market in the Charlotte metro to serve national property manager clients with dual-market exposure." },
  { year: "2021", title: "Eleven Disciplines", body: "Rounded out the full-exterior service catalogue — from awning cleaning to permanent architectural lighting — under a single COI." },
  { year: "2024", title: "$2M Insured, Zero Callbacks", body: "Renewed at $2M general liability plus $1M umbrella. Fourth consecutive quarter of zero client callbacks." },
  { year: "2026", title: "Digital Service Reports", body: "Every service ends with a geotagged, time-stamped PDF report emailed to the client before the truck leaves the block." },
];

const VALUES = [
  { Icon: Shield, title: "Documented", body: "If it isn't in the digital service report, it wasn't billed. Every job carries a photo trail." },
  { Icon: Award, title: "Uniformed", body: "Matte-black vehicle wraps, uniformed technicians, laminated IDs. You always know who is on your property." },
  { Icon: Clock, title: "Punctual", body: "Fifteen-minute arrival windows. Not the two-hour window your last vendor called ‘morning’." },
  { Icon: Users, title: "Named crew", body: "Same lead tech for the life of your contract. No revolving door. No re-learning your building." },
  { Icon: MapPin, title: "Bi-market", body: "New York City and Charlotte metros — a single COI covers your entire dual-market portfolio." },
  { Icon: Zap, title: "Ready", body: "24-hour quote turnaround. Same-day response for retainer clients. Storm re-clean baked into every contract." },
];

const STATS = [
  { value: "11", label: "Years operating" },
  { value: "400+", label: "Contracts served" },
  { value: "$2M", label: "General liability" },
  { value: "0", label: "Callbacks last quarter" },
];

export default function About() {
  const { url } = useMedia();
  return (
    <div className="min-h-screen" data-testid="about-page">
      <PageHero
        eyebrow="About Bespoke"
        title={<>Founded on <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>discipline.</span></>}
        subtitle="Bespoke Exterior Solutions is a US Army veteran-owned commercial maintenance company. We service the exterior of every property to the same standard we held ourselves to in uniform."
        image="/brand/service-van.webp"
        kicker="A short study on why we exist."
      />

      {/* Founder story */}
      <section className="py-28 md:py-36" style={{ background: "var(--bg)" }}>
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14 grid md:grid-cols-12 gap-14 items-start">
          <div className="md:col-span-4 overline pt-2">The Founder</div>
          <div className="md:col-span-8">
            <h2 className="font-display text-4xl md:text-6xl leading-[0.98]" style={{ letterSpacing: "-0.03em" }}>
              A veteran with <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>one clipboard,</span> a water-fed pole, and a promise.
            </h2>
            <div className="mt-10 space-y-6 font-body max-w-2xl" style={{ color: "var(--text-2)", lineHeight: 1.85, fontSize: 17 }}>
              <p>
                Bespoke was founded in 2013 by a US Army veteran who had spent two tours learning that any operation without documentation is an operation waiting to fail. He came home, looked at the commercial cleaning industry — the unmarked trucks, the verbal scopes, the missed windows — and decided the entire vertical needed a rebuild.
              </p>
              <p>
                The first year was one clipboard, one water-fed pole, and one Brooklyn restaurant. Every service was documented in photographs and delivered as a printed report before the crew left the sidewalk. That single ritual — <em>documented, warranted, photographed</em> — is now the operating philosophy behind every one of our four hundred plus contracts.
              </p>
              <p>
                Eleven years later we're a matte-black fleet across two markets, servicing property portfolios that range from single-tenant restaurants to Class-A office towers. But the ritual is the same. Uniformed, punctual, documented, warranted. If it isn't in the report, it wasn't billed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-28 md:py-36" style={{ background: "var(--bg-2)" }}>
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14">
          <div className="grid md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-3 overline pt-2">The Timeline</div>
            <h2 className="md:col-span-9 font-display text-4xl md:text-6xl" style={{ letterSpacing: "-0.03em", lineHeight: 1.02 }}>
              Eleven years, <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>one standard.</span>
            </h2>
          </div>

          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px" style={{ background: "linear-gradient(180deg, transparent, var(--gold), transparent)" }} />
            {TIMELINE.map((t, i) => (
              <motion.div
                key={t.year}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8, delay: i * 0.05 }}
                className={`relative grid md:grid-cols-2 gap-10 mb-14 md:mb-20 ${i % 2 === 0 ? "" : "md:direction-rtl"}`}
                data-testid={`timeline-${t.year}`}
              >
                <div className={`pl-14 md:pl-0 ${i % 2 === 0 ? "md:pr-12 md:text-right" : "md:col-start-2 md:pl-12"}`}>
                  <div className="font-mono text-[10px] uppercase tracking-[0.44em]" style={{ color: "var(--gold)" }}>{t.year}</div>
                  <h3 className="mt-4 font-display text-2xl md:text-4xl" style={{ letterSpacing: "-0.025em", lineHeight: 1.1 }}>{t.title}</h3>
                  <p className="mt-4 font-body" style={{ color: "var(--text-2)", lineHeight: 1.7, fontSize: 15 }}>{t.body}</p>
                </div>
                <div className={`absolute top-2 left-2 md:left-1/2 md:transform md:-translate-x-1/2 w-5 h-5 flex items-center justify-center`} style={{ borderRadius: "var(--r-full)", background: "var(--bg)", border: "2px solid var(--gold)" }}>
                  <div className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--gold)" }} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values grid */}
      <section className="py-28 md:py-36" style={{ background: "var(--bg)" }}>
        <div className="max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-14">
          <div className="grid md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-3 overline pt-2">Our Values</div>
            <h2 className="md:col-span-9 font-display text-4xl md:text-6xl" style={{ letterSpacing: "-0.03em", lineHeight: 1.02 }}>
              Six <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>non-negotiables.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8, delay: i * 0.06 }}
                className="glass-card p-10"
                data-testid={`value-${v.title.toLowerCase()}`}
              >
                <div className="w-14 h-14 flex items-center justify-center" style={{ borderRadius: "var(--r-full)", border: "1px solid var(--line-gold)", background: "rgba(198,168,124,0.06)" }}>
                  <v.Icon size={18} strokeWidth={1.4} style={{ color: "var(--gold)" }} />
                </div>
                <h3 className="mt-6 font-display text-2xl" style={{ letterSpacing: "-0.02em" }}>{v.title}</h3>
                <p className="mt-4 font-body" style={{ color: "var(--text-2)", lineHeight: 1.7, fontSize: 15 }}>{v.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 md:py-32" style={{ background: "var(--bg-2)" }}>
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14 grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-16">
          {STATS.map((s, i) => (
            <motion.div key={s.label} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 1, delay: i * 0.08 }}>
              <div className="font-display leading-none" style={{ fontSize: "clamp(2.6rem, 5vw, 4.5rem)", color: "var(--gold-2)", letterSpacing: "-0.03em" }}>{s.value}</div>
              <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.38em]" style={{ color: "var(--text-3)" }}>{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Equipment / Fleet showcase */}
      <EquipmentFleet />

      {/* Crew · Safety First — real photo */}
      <CrewSafetyFirst />

      <section className="py-28 md:py-36 text-center" style={{ background: "var(--bg)" }}>
        <h2 className="font-display text-5xl md:text-7xl leading-[0.95] max-w-4xl mx-auto px-6" style={{ letterSpacing: "-0.035em" }}>
          Bring the <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>standard</span> to your property.
        </h2>
        <div className="mt-12 flex flex-wrap justify-center gap-5">
          <Link to="/pricing" className="btn-glow" data-testid="about-cta-plans" data-cursor="hover">
            See Instant Plans <ArrowUpRight size={13} />
          </Link>
          <Link to="/quote" className="inline-flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: "var(--text-2)" }} data-testid="about-cta-quote">
            <span className="inline-flex items-center justify-center w-10 h-10" style={{ borderRadius: "var(--r-full)", border: "1px solid var(--line-gold)" }}>
              <ArrowUpRight size={12} style={{ color: "var(--gold)" }} />
            </span>
            Request a Quote
          </Link>
        </div>
      </section>
    </div>
  );
}
